import { useRef, useEffect } from 'react';
import { useUserPreferences } from '@pm2/api';
import { avoidBreakInside } from '@provider/print';
import { units } from '@ui/css';
import { fonts, fontScales } from '@ui/kit/design-tokens';
import { CrossIcon, RoundedCheckmarkIcon, PencilIcon } from '@ui/kit/icons';
import { applyCssDeclarations } from '@utils/css';
import { useStringLength } from '@utils/react';
import { useMatchTaskBreakpoint, taskBreakpoints } from '../../hook.task-breakpoints.js';
import { tokens, wrapper, input, pencil, icon } from './atom.textarea-input.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function TextareaInput(props) {
  const wrapperRef = useRef(null);
  const textareaRef = useRef(null);
  const isMobile = useMatchTaskBreakpoint(taskBreakpoints.mobile);
  const preferences = useUserPreferences();
  const {
    fontSize = 18
  } = props;
  const font = preferences.font === "default" ? fonts.proxima : fonts.dyslexie;
  const valueLengthInPx = useStringLength(props.value ?? "", {
    size: fontSize * (preferences.font === "dyslexie" ? fontScales.dyslexie : fontScales.proxima),
    family: String(font.fontFaces.bold.family.defaultValue),
    weight: String(font.fontFaces.bold.weight.defaultValue)
  });
  const minLengthInPx = useStringLength("0".repeat(props.minimumInputLength ?? 0), {
    size: fontSize * (preferences.font === "dyslexie" ? fontScales.dyslexie : fontScales.proxima),
    family: String(font.fontFaces.bold.family.defaultValue),
    weight: String(font.fontFaces.bold.weight.defaultValue)
  });
  const icon$1 = (() => {
    switch (props.state) {
      case "correct":
        return /*#__PURE__*/jsx(RoundedCheckmarkIcon, {});
      case "incorrect":
        return /*#__PURE__*/jsx(CrossIcon, {
          strokeWidth: 1.5,
          height: 10
        });
    }
  })();
  const handleChange = evt => {
    props.onChange?.(evt);
    const textarea = evt.currentTarget;
    setTimeout(() => {
      // NB: We reset the height to ensure that calculating scrollHeight
      // afterwards reflects the actual content height, and not the height
      // previously set.
      applyCssDeclarations(tokens.height.__override(0), wrapperRef);
      applyCssDeclarations(tokens.height.__override(`${textarea.scrollHeight + 4}px`), wrapperRef);
    });
  };
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    applyCssDeclarations(tokens.height.__override(0), wrapperRef);
    applyCssDeclarations(tokens.height.__override(`${textarea.scrollHeight + 4}px`), wrapperRef);
  }, [isMobile]);
  return /*#__PURE__*/jsxs("span", {
    ref: wrapperRef,
    className: wrapper,
    style: tokens.__override({
      width: units.px(Math.max(minLengthInPx, valueLengthInPx)),
      maxWidth: props.maxWidth,
      fontSize: units.px(fontSize)
    }),
    "data-state": props.state,
    "data-empty": !props.value?.trim(),
    ...avoidBreakInside(),
    children: [/*#__PURE__*/jsx("textarea", {
      ref: textareaRef,
      className: input,
      readOnly: props.readOnly,
      enterKeyHint: "next",
      value: props.value ?? "",
      onChange: handleChange,
      inputMode: props.inputMode,
      spellCheck: false,
      autoCapitalize: "none",
      autoComplete: "off",
      autoCorrect: "off"
    }), props.state === "input" && /*#__PURE__*/jsx(PencilIcon, {
      className: pencil
    }), icon$1 && /*#__PURE__*/jsx("span", {
      className: icon,
      children: icon$1
    })]
  });
}

export { TextareaInput };
//# sourceMappingURL=atom.textarea-input.js.map
